/**
 * Clicking a named pin asks "travel there?" before anything happens (Maz, 2026-10-21: click to confirm). This is
 * the words for that question, from the server's dice-free preview (`GET /journey-preview`): what it says and what
 * it does not. The lost and wandering-encounter rolls are secret and never appear here.
 */

import { titleLabel } from '../labels.js';

export type JourneyPreviewView =
  | {
      readonly kind: 'journey';
      readonly destinationName: string;
      readonly miles: number;
      readonly days: number;
      readonly terrain: string;
      readonly route?: string;
    }
  | { readonly kind: 'exit' | 'blocked' | 'uncosted' | 'here'; readonly destinationName: string; readonly note: string };

/** "less than a day", "1 day", "2.5 days" — a march is read in days, and a fraction is worth one decimal. */
export function daysPhrase(days: number): string {
  if (days < 1) return 'less than a day';
  const rounded = Math.round(days * 10) / 10;
  return rounded === 1 ? '1 day' : `${rounded} days`;
}

/** The sentence a confirmation shows. */
export function describePreview(preview: JourneyPreviewView): string {
  if (preview.kind !== 'journey') return preview.note;
  const ground = titleLabel(preview.terrain).toLowerCase();
  const via = preview.route ? `, following ${titleLabel(preview.route).toLowerCase()}` : '';
  return `About ${preview.miles} ${preview.miles === 1 ? 'mile' : 'miles'}, ${daysPhrase(preview.days)} over ${ground}${via}.`;
}

/** Whether the confirmation offers to go: not for the place already here, nor one the party cannot reach. */
export function canTravel(preview: JourneyPreviewView | undefined): boolean {
  return preview !== undefined && preview.kind !== 'blocked' && preview.kind !== 'here';
}
